import { Box, Stack } from '@mui/material';
import { addMonths, parseISO, addDays } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { MilestoneDto } from 'tdc-web-backend/milestones/schemas';
import { primaryDark, secondaryBlue } from '../../../../../utils/color';
import { simpleDateFormatter } from '../../../../../utils/helpers';
import CustomButton from '../../../../../components/button/CustomButton';
import AddMilestoneModal from './components/AddMilestoneModal';
import MilestoneItem from './components/MilestoneItem';

const MilestonesForm = ({ disabled = false }: { disabled?: boolean }) => {
  const { control, getValues, setValue, watch, trigger } = useFormContext();

  const { fields, remove, append } = useFieldArray({
    control,
    name: 'body.milestones',
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Milestone display inherited value converter
  const updateBudget = () => {
    const milestones = getValues('body.milestones');
    const totalBudget: number = milestones.reduce((acc: number, milestone: MilestoneDto) => {
      if (milestone.budget === null) return acc;
      return acc + milestone.budget;
    }, 0);

    setValue(`initialAmount`, totalBudget);
  };

  const removeMilestone = (index: number) => {
    remove(index);

    // Force dirty check for body.milestones to update formState in ContractEditor
    const currentMilestones = getValues('body.milestones');
    setValue('body.milestones', currentMilestones, {
      shouldDirty: true,
      shouldValidate: true,
    });

    updateBudget();
  };

  const setEndDateForRecurring = (index: number, value: number) => {
    const startDate = parseISO(getValues(`body.milestones.${index}.start`));
    if (!isNaN(startDate.valueOf())) {
      startDate.setMonth(startDate.getMonth() + value);
      setValue(`body.milestones.${index}.end`, startDate.toISOString());
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        const lastIndex = getValues('body.milestones').length - 1;

        if (name === 'body.milestones.0.start') {
          setValue(
            'body.projectDurationFrom',
            simpleDateFormatter(parseISO(getValues('body.milestones.0.start'))),
          );
        }

        if (name === `body.milestones.${lastIndex}.end`) {
          const isRecurring = getValues(`body.milestones.${lastIndex}.recurring`);
          if (isRecurring) {
            const lastStartDate = getValues(`body.milestones.${lastIndex}.start`);
            setValue(
              'body.projectDurationTo',
              getValues(`body.milestones.${lastIndex}.duration`)
                ? simpleDateFormatter(
                    addMonths(
                      parseISO(lastStartDate),
                      getValues(`body.milestones.${lastIndex}.duration`),
                    ),
                  )
                : '',
            );
          } else {
            setValue(
              'body.projectDurationTo',
              simpleDateFormatter(getValues(`body.milestones.${lastIndex}.end`)),
            );
          }
        }

        if (name.startsWith('body.milestones.') && name.endsWith('.duration')) {
          const parts = name.split('.');
          const index = +parts[2];
          const { duration } = value.body.milestones[index];
          setEndDateForRecurring(index, duration);
        }
        if (name.startsWith('body.milestones.') && name.endsWith('.recurring')) {
          const parts = name.split('.');
          const index = +parts[2];
          setValue(`body.milestones.${index}.duration`, null);
          setValue(`body.milestones.${index}.end`, '');
          if (getValues(`body.milestones.${index}.recurring`)) {
            setTimeout(() => {
              trigger(`body.milestones.${index}.duration`);
            });
          } else {
            setTimeout(() => {
              trigger(`body.milestones.${index}.end`);
            });
          }
        }
        if (name.startsWith('body.milestones.') && name.endsWith('.budget')) {
          updateBudget();
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Stack>
      <Box sx={{ mb: 5 }}>
        <Stack spacing={2}>
          {fields.map((item, index) => (
            <MilestoneItem
              key={item.id}
              index={index}
              onRemove={removeMilestone}
              disabled={disabled}
              totalMilestones={fields.length}
            />
          ))}
        </Stack>
      </Box>

      <Stack alignItems="center" justifyContent="center">
        <CustomButton
          disabled={disabled}
          onClick={() => setIsModalOpen(true)}
          variant="primaryText"
          startIcon={
            <Stack
              sx={{
                width: '24px',
                height: '24px',
                backgroundColor: 'primaryLight.500',
                borderRadius: '2px',
              }}
            >
              <AddIcon
                sx={{
                  color: '#fff',
                }}
              />
            </Stack>
          }
          sx={{
            border: `1px solid ${secondaryBlue[100]}`,
            color: 'primaryLight.500',
            bgcolor: 'primaryDark.150',
            borderRadius: '4px',
            textTransform: 'uppercase',
            fontSize: '12px',
            fontWeight: 600,
            padding: '10px 12px',
            boxShadow: `6px 6px 15px rgba(${primaryDark[600]}, 0.1)`,
            '&:hover': {
              bgcolor: 'primaryLight.100',
              borderColor: 'transparent',
            },
          }}
        >
          New Milestone
        </CustomButton>
      </Stack>

      <AddMilestoneModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={(newMilestones) => {
          append(newMilestones);

          // Force dirty check for body.milestones to update formState in ContractEditor
          const currentMilestones = getValues('body.milestones');
          setValue('body.milestones', currentMilestones, {
            shouldDirty: true,
            shouldValidate: true,
          });

          const addedBudget = newMilestones.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc: number, m: any) => acc + (m.budget || 0),
            0,
          );
          setValue('initialAmount', (watch('initialAmount') || 0) + addedBudget);
        }}
        minStartDate={(() => {
          const milestones = getValues('body.milestones');
          if (milestones && milestones.length > 0) {
            const last = milestones[milestones.length - 1];
            if (last && last.end) {
              return addDays(parseISO(last.end), 1);
            }
          }
          return null;
        })()}
        currency={watch('body.scope.currency')}
        currentProjectBudget={watch('initialAmount') || 0}
      />
    </Stack>
  );
};

export default MilestonesForm;
